import type { MouseEvent, ReactNode } from 'react';

import { navigate } from './router.js';

/**
 * A link that moves inside the app, and is still a real link.
 *
 * The same eight lines were written out in the app bar, the breadcrumb and the
 * portfolio's attention list, and the cross-links added in the redesign would
 * have made it a dozen. The behaviour matters and is easy to get half right:
 *
 *   * it is an `<a href>`, so it has a target the browser can show, open in a
 *     new tab, bookmark, and read out as a link;
 *   * a plain left-click navigates in place, without a page reload;
 *   * Ctrl/Cmd/Shift-click and middle-click keep the browser's own behaviour,
 *     because a real href should open in a new tab when somebody asks it to.
 */
export function AppLink({
  to,
  className,
  children,
  onNavigate,
  ...rest
}: {
  readonly to: string;
  readonly className?: string;
  readonly children: ReactNode;
  /** Runs after the move — for a parent that has to close a menu. */
  readonly onNavigate?: () => void;
  readonly 'aria-current'?: 'page' | undefined;
  readonly title?: string;
}): JSX.Element {
  function click(event: MouseEvent<HTMLAnchorElement>): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(to);
    onNavigate?.();
    scrollToHash(to);
  }

  return (
    <a href={to} className={className} onClick={click} {...rest}>
      {children}
    </a>
  );
}

/** How long to keep looking for the anchor before giving up, and how often. */
const HASH_WAIT_MS = 2500;
const HASH_POLL_MS = 60;

/**
 * Take the reader to the `#anchor` in the href, because nothing else will.
 *
 * `preventDefault` above is what makes this a single-page move rather than a
 * reload — and it is also what silently switches OFF the browser's own jump to
 * an anchor. `pushState` does not scroll either. So a link ending in `#…` moved
 * the address bar and left the reader exactly where they were.
 *
 * That is not hypothetical: every gate item on the Award stage points at a
 * block on the OVERVIEW, which is the page the reader is already standing on,
 * so "the milestones" did nothing whatsoever when pressed. Found by the first
 * person to follow the panel's own instruction.
 *
 * IT POLLS, AND THE FIRST VERSION OF THIS DID NOT.
 *
 * That version waited two animation frames, on the reasoning that one fires
 * before React's paint and the second after it — which is true, and which was
 * still far too early. Following a gate link usually changes the SECTION, and
 * the register that lands there fetches its rows before it renders anything
 * with an id on it. Measured in the browser rather than reasoned about: the
 * URL changed, the target existed 1,530px down the page, and the scroll
 * position was still 0. The anchor had simply not been born yet when the two
 * frames elapsed.
 *
 * So it looks every `HASH_POLL_MS` until the element appears or `HASH_WAIT_MS`
 * passes. A register that never loads leaves the reader where they were, which
 * is the behaviour they had before any of this existed.
 *
 * A TIMER RATHER THAN `requestAnimationFrame`, and the reason is not style.
 * What is being waited on is a FETCH, not a paint, so frames were never the
 * right unit. And rAF does not fire at all while a tab is hidden — which is
 * how the second version of this was caught: `document.hidden` was true in the
 * pane it was being tested in, the callback never ran once, and the code read
 * as correct while doing nothing. A user clicking a link is by definition
 * looking at the tab, so rAF would have worked in the product; it would just
 * never have been verifiable.
 *
 * IT ABANDONS IF THE READER MOVES ON. Someone who clicks a gate link and then
 * immediately clicks something else must not be yanked back two seconds later
 * by a scroll they have forgotten asking for.
 *
 * FOCUS MOVES TOO, not just the scroll position. A keyboard or screen-reader
 * user who is only scrolled has not been taken anywhere — their next Tab
 * carries on from the link they pressed, back up the page. `tabIndex = -1`
 * makes a plain container focusable for this without adding it to the tab
 * order; `preventScroll` stops the browser doing its own second jump.
 */
function scrollToHash(to: string): void {
  const at = to.indexOf('#');
  if (at === -1) return;
  const hash = to.slice(at + 1);
  if (!hash) return;

  const deadline = Date.now() + HASH_WAIT_MS;

  const look = (): void => {
    // The reader has gone somewhere else since. Their choice wins.
    if (window.location.hash.slice(1) !== hash) return;

    const target = document.getElementById(hash);
    if (!target) {
      if (Date.now() < deadline) setTimeout(look, HASH_POLL_MS);
      return;
    }

    /*
     * `auto`, NOT `smooth`, and it is a decision rather than the default.
     *
     * This function exists to put back the jump `preventDefault` took away, and
     * the browser's own jump is instant — so instant is the behaviour being
     * restored rather than a new one being invented. It also avoids animating
     * fifteen hundred pixels, which on the gate links is the usual distance and
     * is more disorienting than useful.
     *
     * It removes a `prefers-reduced-motion` branch this would otherwise need
     * (`home-projects.css` already forces `scroll-behavior: auto` for those
     * readers, and a JS `smooth` would have ignored it), and it is the only
     * form that can be verified in a hidden tab — a smooth scroll does not
     * animate in one, which is how the version before this looked correct,
     * focused the right element, and moved nothing.
     */
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
    target.tabIndex = -1;
    target.focus({ preventScroll: true });
  };

  look();
}
